/**
 * DirectModal Component
 * 
 * A simplified modal component specifically for the AlertManager that renders directly
 * in the DOM using createPortal. This approach guarantees the modal will appear on top
 * of all other content regardless of stacking contexts or z-index issues.
 * 
 * Uses the centralized CSS theming approach with separate dark and light theme styles.
 * 
 * @module DirectModal
 */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

// Import theme CSS files
import '../../styles/themes/dark/AlertManager/DirectModal.css';
import '../../styles/themes/light/AlertManager/DirectModal.css';

/**
 * DirectModal component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.title - Title to display in the modal header
 * @param {React.ReactNode} props.children - Content to display in the modal
 * @param {string} props.size - Size of the modal ('small', 'medium', 'large')
 * @param {React.ReactNode} props.footer - Custom footer content
 * @returns {JSX.Element|null} Modal component or null if not open
 */
function DirectModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'medium',
  footer
}) {
  // Create or get modal root element - moved outside conditional
  const getOrCreateModalRoot = () => {
    let root = document.getElementById('direct-modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'direct-modal-root';
      document.body.appendChild(root);
    }
    return root;
  };
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
      document.body.style.overflow = '';
    }
  };
  
  // Handle escape key and body scrolling - hooks must be called unconditionally
  useEffect(() => {
    // Add keyboard event listener for Escape key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
        document.body.style.overflow = '';
      }
    };
    
    // Manage body scroll
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Reset body overflow when component unmounts or modal closes
      if (isOpen) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, onClose]);
  
  // Don't render anything if modal is not open
  if (!isOpen) return null;
  
  // Modal element to be rendered
  const modalElement = (
    <div 
      className="direct-modal-backdrop" 
      onClick={handleBackdropClick}
    >
      <div className={`direct-modal-container direct-modal-${size}`}>
        <div className="direct-modal-header">
          <h2 className="direct-modal-title">{title}</h2>
          <button 
            className="direct-modal-close-button"
            onClick={() => {
              onClose();
              document.body.style.overflow = '';
            }}
            aria-label="Close modal"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        
        <div className="direct-modal-content">
          {children}
        </div>
        
        {footer && (
          <div className="direct-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
  
  // Use createPortal to render the modal directly in the DOM
  return ReactDOM.createPortal(modalElement, getOrCreateModalRoot());
}

export default DirectModal;
